import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '@/environments/environment';

/**
 * Which stage of the 1099-NEC filing obligation a creator-year sits in.
 * Mirrors `TaxReadinessBucket` in the API — keep the two in step.
 */
export type TaxReadinessBucket =
  | 'no_tax_form'
  | 'ready_to_file'
  | 'filed'
  | 'below_threshold';

export type TaxProfileStatus = 'missing' | 'submitted' | 'verified' | 'invalid';

export type TaxFilingStatus = 'not_applicable' | 'pending' | 'filed' | 'corrected';

export interface TaxReadinessRow {
  blazeFirebaseUid: string;
  creatorName: string;
  email: string | null;
  bankCountryCode: string | null;
  /** False means Airwallex may not associate this creator's transfers with their W-9. */
  externalIdentifierOk: boolean;
  taxFormType: string | null;
  profileStatus: TaxProfileStatus;
  deliveryPreference: string | null;
  totalMinor: number;
  paymentCount: number;
  thresholdMinor: number;
  bucket: TaxReadinessBucket;
  filingStatus: TaxFilingStatus | null;
  filedDate: string | null;
  reportedAmountMinor: number | null;
  /** Non-USD money paid to a US payee — excluded from Box 1, surfaced so it isn't lost. */
  nonUsdMinor: number;
}

export interface TaxReadinessFilter {
  taxYear: number;
  bucket?: TaxReadinessBucket;
  searchTerm?: string;
  skip?: number;
  take?: number;
}

export interface TaxReadinessResponse {
  rows: TaxReadinessRow[];
  totalCount: number;
}

export interface TaxReadinessStats {
  no_tax_form: number;
  ready_to_file: number;
  filed: number;
  below_threshold: number;
  totalMinor: number;
}

export interface FilingEntry {
  blazeFirebaseUid: string;
  status?: TaxFilingStatus;
  filedDate?: string;
  reportedAmountMinor?: number;
  airwallexReference?: string;
  notes?: string;
}

export interface RecordFilingsResponse {
  recorded: number;
  /** Creator-years where our ledger and Airwallex's reported figure disagree. */
  mismatches: string[];
}

/**
 * Tax operations console API.
 *
 * Note what is absent: any way to *file*. Airwallex exposes no filing API — an operator
 * files in the Airwallex web app (Transfers → Tax Filing). This service supports the work
 * around that: seeing who is ready, exporting our ledger to diff against theirs, and
 * recording what came back so the creator app can show a real filing date.
 */
@Injectable({
  providedIn: 'root',
})
export class AdminCreatorTaxService {
  private http = inject(HttpClient);
  private base = `${environment.shout_api.url}/creator-tax/admin`;

  getReadiness(filter: TaxReadinessFilter): Observable<TaxReadinessResponse> {
    return this.http.post<TaxReadinessResponse>(`${this.base}/readiness`, filter);
  }

  getStats(taxYear: number): Observable<TaxReadinessStats> {
    return this.http.get<TaxReadinessStats>(`${this.base}/years/${taxYear}/stats`);
  }

  /** Records what Airwallex filed. This is what unlocks a year in the creator app. */
  recordFilings(taxYear: number, filings: FilingEntry[]): Observable<RecordFilingsResponse> {
    return this.http.post<RecordFilingsResponse>(`${this.base}/filings`, { taxYear, filings });
  }

  /**
   * Records Airwallex's IRS TIN-matching outcome. Airwallex gives us no webhook for it, so
   * an operator reads it in the Airwallex web app and records it here; `invalid` locks the
   * creator's tax years and prompts them to resubmit.
   */
  setProfileStatus(
    blazeFirebaseUid: string,
    status: TaxProfileStatus,
  ): Observable<{ status: TaxProfileStatus }> {
    return this.http.post<{ status: TaxProfileStatus }>(`${this.base}/profile-status`, {
      blazeFirebaseUid,
      status,
    });
  }

  /** Checks whether Airwallex holds the right `external_identifier` for a creator. */
  syncBeneficiary(creatorId: string): Observable<{ ok: boolean; externalIdentifier: string | null }> {
    return this.http.post<{ ok: boolean; externalIdentifier: string | null }>(
      `${this.base}/beneficiary-sync/${creatorId}`,
      {},
    );
  }

  /** CSV comes back as text, not JSON — the caller turns it into a download. */
  exportReadinessCsv(taxYear: number): Observable<string> {
    return this.http.get(`${this.base}/years/${taxYear}/export.csv`, { responseType: 'text' });
  }

  exportPostalCsv(taxYear: number): Observable<string> {
    return this.http.get(`${this.base}/years/${taxYear}/postal.csv`, { responseType: 'text' });
  }
}
